/**
 * dcent-header-pin.js — 헤더 고정 전환을 **점프 없이** 앞당긴다 (모바일 MAGE-601 · 데스크탑 MAGE-654).
 *
 * ── 무엇이 문제였나 ──────────────────────────────────────────────────────────
 * 원본(Layout.tsx + Header.tsx)에는 sticky 전환이 아예 없다. 공지바와 헤더가 처음부터 둘 다
 * `position: fixed` 오버레이이고, 스크롤 8px 을 넘으면 공지바는 위로 슬라이드 아웃하고 헤더는
 * `top` 이 70 → 12 로 300ms 애니메이션한다. 흐름에서 빠진 적이 없으니 잘림도 점프도 없다.
 *
 * 이 테마는 그 겉모습만 이식하고 벤더 Minimog 의 sticky 기구를 그대로 뒀다. 공지바·헤더가
 * 문서 흐름 안에 있고, 벤더 header.js 가 `scrollY > 20 + 공지바높이(71) = 91` 에서 `stuck` 을
 * 붙여 헤더만 fixed 로 바꾼다. 그 지점에서 알약 위치가 **불연속으로 튄다** — 그전 몇 px 구간은
 * 알약 윗부분이 뷰포트 밖으로 잘리기까지 한다. 사용자가 "메뉴가 숨쉬듯 커졌다 작아졌다" 고
 * 본 것이 이것이다(2026-08-30 실측: 알약 치수 287x50 불변, top 만 −2.5 → 8 로 점프).
 *
 * ── 어떻게 고치나 ────────────────────────────────────────────────────────────
 * 벤더보다 **먼저** 자체 클래스(`dcent-hd-pinned`)를 붙인다. 붙이는 지점을 "흐름상 알약 top 이
 * 고정 상태의 알약 top 과 같아지는 순간" 으로 잡으면 전환이 연속이 된다:
 *
 *     흐름상 알약 top(뷰포트) = restTop − scrollY + restPad
 *     고정 시 알약 top        = 0 + pinnedPad
 *     →  임계값 scrollY = restTop − (pinnedPad − restPad)
 *
 * 값을 하드코딩하지 않고 **실측**한다 — 공지바는 문구 길이·표시 여부에 따라 높이가 달라지고,
 * 여백 값도 CSS 가 바꿀 수 있다. 고정 상태에서는 wrapper 가 fixed 라 흐름 위치를 못 재므로
 * **풀린 상태에서만** 다시 잰다.
 *
 * 벤더의 `stuck` 과는 충돌하지 않는다 — 같은 `position: fixed; top: 0` 을 주고, 벤더는 91 에서
 * 자기 클래스를 따로 붙였다 뗀다(멱등). 우리 클래스는 71.5 부터 91 사이의 빈 구간만 메운다.
 *
 * ── 데스크탑도 같은 기구를 탄다 (2026-10-12 MAGE-654 · MAGE-653 1번) ───────────────
 * 처음엔 "데스크탑은 같은 증상이 없다" 고 보고 ≥1024 를 제외했는데, 리브랜딩 오픈 QC 에서
 * 데스크탑 PDP·홈의 "스크롤 시 상단이 벌어지며 흰 배경 노출 / 스크롤 업 때 GNB 가 툭툭
 * 걸림" 이 접수됐고 실측(2026-10-12, 1440 폭)으로 같은 점프가 확인됐다: 알약 흐름 top 42,
 * 벤더 임계값 20 + 공지바 40 = 60 → scrollY 42~60 구간에서 알약이 최대 18px 뷰포트 밖으로
 * 잘렸다가 stuck 순간 top 0 으로 **튀어 내려온다**(풀릴 때는 반대로 튀어 오른다). 그 18px
 * 구간이 "벌어진 상단" 이고, 그 자리는 헤더 바의 투명 여백이라 뒤 페이지(흰 바탕)가 비친다.
 * 데스크탑 바(.m-header__desktop)는 고정 전후 여백이 같아(--dcent-hd-gap-y 14) padDelta 가 0
 * 이다 — 임계값 = 알약 흐름 top 그 자체. 모바일은 종전대로 8 → 16 차이를 보정한다.
 * JS 가 못 뜨면 종전 동작(벤더 stuck 단독)으로 조용히 되돌아간다 — CSS 가 `.stuck` 에도 같은
 * 값을 갖고 있어 여백 16 은 유지된다.
 *
 * 양 샵 byte-identical — assets/dcent-* 는 parity 게이트 분모다.
 */
(function () {
  var MQ = "(max-width: 1023px)";
  var PINNED = "dcent-hd-pinned";

  function init() {
    var header = document.querySelector(".m-header");
    if (!header) return;
    var wrapper = header.querySelector(".m-header__wrapper");
    var mobileBar = header.querySelector(".m-header__mobile");
    var desktopBar = header.querySelector(".m-header__desktop");
    if (!wrapper || (!mobileBar && !desktopBar)) return;

    var mq = window.matchMedia(MQ);
    /** 지금 보이는 바 — 모바일(<1024)은 .m-header__mobile, 데스크탑은 .m-header__desktop.
     *  여백 실측(restPad)과 목표 여백 계산이 이 바를 기준으로 한다. */
    function activeBar() {
      return mq.matches ? mobileBar || desktopBar : desktopBar || mobileBar;
    }
    var restTop = null; // 문서 기준 wrapper 흐름 위치 (풀린 상태에서만 유효)
    var restPad = null; // 평상시 상단 여백 (풀린 상태에서만 유효)
    var pinned = false;

    /** 고정 시 상단 여백 − 평상시 상단 여백.
     *  ⚠️ **상태에 의존하면 안 된다.** 처음엔 pinned 일 때 0 을 돌려줬더니 임계값이 상태에 따라
     *  71.5 ↔ 79.5 로 오가며 클래스가 진동했다(실측: 붙었다 떨어졌다 반복 + 6.4px 점프 잔존).
     *  두 값 모두 **풀린 상태에서 캐시**해 두고 상태와 무관하게 같은 임계값을 낸다. */
    function padDelta() {
      if (restPad === null) return 0;
      // 데스크탑 바는 고정 전후 여백이 같다(--dcent-hd-gap-y) → 보정 0. 모바일만 목표 여백을 읽는다.
      if (!mq.matches) return 0;
      var target = parseFloat(
        getComputedStyle(header).getPropertyValue("--dcent-hd-pinned-pt"),
      );
      return isNaN(target) ? 0 : target - restPad;
    }

    function measure() {
      if (pinned) return; // fixed 상태에선 흐름 위치를 못 잰다
      var bar = activeBar();
      restTop = wrapper.getBoundingClientRect().top + window.scrollY;
      restPad = bar ? parseFloat(getComputedStyle(bar).paddingTop) || 0 : 0;
    }

    /** 브레이크포인트가 바뀌면 바와 여백이 달라진다 — 풀고 다시 재서 적용한다. */
    function onBreakpointChange() {
      if (pinned) {
        pinned = false;
        header.classList.remove(PINNED);
      }
      restTop = null;
      restPad = null;
      measure();
      apply();
    }

    function apply() {
      if (restTop === null) measure();
      if (restTop === null) return;
      var threshold = restTop - padDelta();
      var want = window.scrollY >= threshold;
      if (want === pinned) {
        if (!pinned) measure(); // 공지바 높이 변화 추적
        return;
      }
      pinned = want;
      header.classList.toggle(PINNED, want);
      if (!want) measure();
    }

    measure();
    apply();
    window.addEventListener("scroll", apply, { passive: true });
    window.addEventListener("resize", function () {
      if (!pinned) measure();
      apply();
    });
    if (mq.addEventListener) mq.addEventListener("change", onBreakpointChange);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
